/**
 * @fileoverview Local-first Dexie query engine for report and dashboard widget queries.
 * @module local-db/reporting/dexieQueryEngine
 *
 * Responsibilities: - Execute `DexieQuery` specifications against local tenant Dexie collections
 *                     (`cases`, `entities`, `contacts`, `violations`) with filtering, time-dimension
 *                     truncation, grouping, measure aggregation, sorting, and limits.
 * Dependencies:     - ../databases/tenantDb (TenantDatabase)
 *                   - ./reportingTypes (DexieQuery, DexieQueryResult, DexieQueryFilter, etc.)
 * Architecture:     - Local-first: pure Dexie read; zero network/API calls. Safe to invoke
 *                     inside `liveQuery` observed functions.
 * Notes:            - Supports both `db.collections[name].find().exec()` and `db[name].toArray()`
 *                     table shapes, and excludes soft-deleted (`_deleted === true`) documents.
 */

// ---------------------------------------------------------------------------
// DexieQueryEngine — executes report queries against local Dexie collections
// Used by the Report Builder live preview (offline-capable, no network needed).
// Server-side Cube.dev is used for scheduled/exported reports only.
// ---------------------------------------------------------------------------

import { unwindViolationRows } from '@firesquirrel/report-template-resolver';
import type { TenantDatabase } from '../databases/tenantDb';
import type {
  DexieQuery,
  DexieQueryResult,
  DexieQueryFilter,
  ColumnMeta,
  FilterOperator,
} from './reportingTypes';

// ---------------------------------------------------------------------------
// Filter evaluation
// ---------------------------------------------------------------------------

function getNestedValue(obj: Record<string, any>, path: string): any {
  return path.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
}

function toNumericOrComparable(a: any, b: any): [number | string, number | string] {
  if (
    a !== null &&
    a !== undefined &&
    b !== null &&
    b !== undefined &&
    a !== '' &&
    b !== '' &&
    typeof a !== 'boolean' &&
    typeof b !== 'boolean'
  ) {
    const numA = Number(a);
    const numB = Number(b);
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
      return [numA, numB];
    }
  }
  return [a, b];
}

export function evaluateFilter(doc: Record<string, any>, filter: DexieQueryFilter): boolean {
  let val = getNestedValue(doc, filter.field);
  if ((val === undefined || val === null) && filter.fallbackFields) {
    for (const fallback of filter.fallbackFields) {
      const fallbackValue = getNestedValue(doc, fallback);
      if (fallbackValue !== undefined && fallbackValue !== null) {
        val = fallbackValue;
        break;
      }
    }
  }
  const { operator, values } = filter;

  switch (operator as FilterOperator) {
    case 'equals':
      return values.some(v => String(val) === String(v));
    case 'notEquals':
      return values.every(v => String(val) !== String(v));
    case 'contains':
      return typeof val === 'string' && values.some(v => val.toLowerCase().includes(String(v).toLowerCase()));
    case 'notContains':
      return typeof val === 'string' && values.every(v => !val.toLowerCase().includes(String(v).toLowerCase()));
    case 'gt': {
      if (val == null) return false;
      const [lhs, rhs] = toNumericOrComparable(val, values[0]);
      return lhs > rhs;
    }
    case 'gte': {
      if (val == null) return false;
      const [lhs, rhs] = toNumericOrComparable(val, values[0]);
      return lhs >= rhs;
    }
    case 'lt': {
      if (val == null) return false;
      const [lhs, rhs] = toNumericOrComparable(val, values[0]);
      return lhs < rhs;
    }
    case 'lte': {
      if (val == null) return false;
      const [lhs, rhs] = toNumericOrComparable(val, values[0]);
      return lhs <= rhs;
    }
    case 'set':
      return val != null && val !== '' && val !== false;
    case 'notSet':
      return val == null || val === '' || val === false;
    case 'inDateRange': {
      if (!val) return false;
      const d = new Date(val).getTime();
      const from = values[0] ? new Date(values[0]).getTime() : -Infinity;
      const to = values[1] ? new Date(values[1]).getTime() : Infinity;
      return d >= from && d <= to;
    }
    case 'beforeDate':
      return val != null && new Date(val).getTime() < new Date(values[0]).getTime();
    case 'afterDate':
      return val != null && new Date(val).getTime() > new Date(values[0]).getTime();
    default:
      return true;
  }
}

// ---------------------------------------------------------------------------
// Date range helpers for timeDimension
// ---------------------------------------------------------------------------

function resolveDateRange(dateRange: string | [string, string] | undefined): [Date, Date] | null {
  if (!dateRange) return null;
  if (Array.isArray(dateRange)) {
    return [new Date(dateRange[0]), new Date(dateRange[1])];
  }
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (dateRange) {
    case 'Today':
      return [today, new Date(today.getTime() + 86_400_000 - 1)];
    case 'This week': {
      const dow = today.getDay();
      const start = new Date(today.getTime() - dow * 86_400_000);
      return [start, new Date(start.getTime() + 7 * 86_400_000 - 1)];
    }
    case 'This month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      return [start, end];
    }
    case 'This quarter': {
      const q = Math.floor(now.getMonth() / 3);
      const start = new Date(now.getFullYear(), q * 3, 1);
      const end = new Date(now.getFullYear(), q * 3 + 3, 0, 23, 59, 59, 999);
      return [start, end];
    }
    case 'This year': {
      const start = new Date(now.getFullYear(), 0, 1);
      const end = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
      return [start, end];
    }
    case 'Last 7 days':
      return [new Date(today.getTime() - 6 * 86_400_000), new Date(today.getTime() + 86_400_000 - 1)];
    case 'Last 30 days':
      return [new Date(today.getTime() - 29 * 86_400_000), new Date(today.getTime() + 86_400_000 - 1)];
    case 'Last 90 days':
      return [new Date(today.getTime() - 89 * 86_400_000), new Date(today.getTime() + 86_400_000 - 1)];
    case 'Last 12 months': {
      const start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      return [start, now];
    }
    default:
      return null;
  }
}

function truncateToGranularity(date: Date, granularity: string): string {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  switch (granularity) {
    case 'day':   return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    case 'week': {
      const dow = date.getDay();
      const monday = new Date(date.getTime() - dow * 86_400_000);
      return `${monday.getFullYear()}-W${String(Math.ceil((monday.getDate()) / 7)).padStart(2, '0')}`;
    }
    case 'month': return `${y}-${String(m + 1).padStart(2, '0')}`;
    case 'quarter': return `${y}-Q${Math.floor(m / 3) + 1}`;
    case 'year':  return String(y);
    default:      return date.toISOString().slice(0, 10);
  }
}

/**
 * Resolve a dimension value for a document, consulting `fallbackFields` in order
 * when the primary `field` resolves to undefined/null — e.g. `status.name` →
 * `status.code` on lookup-reference docs that carry a code without a name.
 */
function resolveDimensionValue(
  doc: Record<string, any>,
  field: string,
  fallbackFields?: string[],
): any {
  const value = getNestedValue(doc, field);
  if (value !== undefined && value !== null) return value;
  for (const fallback of fallbackFields ?? []) {
    const fallbackValue = getNestedValue(doc, fallback);
    if (fallbackValue !== undefined && fallbackValue !== null) return fallbackValue;
  }
  // Canonical addresses split the street line into parts
  // (entityLocation.address.streetNumber/streetName/streetSuffix per
  // entityLocationAddressSchema) — a `…address.street` dimension never
  // resolves on those docs. Compose the line from parts, checking the
  // parent object of the primary field AND every fallback path, so
  // canonical and migrated records render "123 Main St" instead of
  // grouping under blank.
  if (field.endsWith('.street')) {
    for (const candidate of [field, ...(fallbackFields ?? [])]) {
      if (!candidate.endsWith('.street')) continue;
      const parent = getNestedValue(doc, candidate.slice(0, -'.street'.length));
      if (parent && typeof parent === 'object') {
        const a = parent as Record<string, unknown>;
        const composed = [a.streetNumber, a.streetName, a.streetSuffix]
          .filter((p) => typeof p === 'string' && p !== '')
          .join(' ');
        if (composed) return composed;
      }
    }
  }
  return value;
}

function matchesMeasureCondition(doc: Record<string, any>, outputKey: string): boolean {
  if (outputKey === 'InspectableEntities.activeCount') {
    const status = String(doc.status?.name ?? doc.status?.code ?? '');
    return status.toLowerCase() === 'active';
  }
  if (outputKey === 'InspectionSeries.openCount') {
    const status = String(doc.status ?? doc.caseStatus ?? '');
    return ['active', 'notStarted', 'open', 'in_progress'].includes(status);
  }
  if (outputKey === 'InspectionSeries.closedCount') {
    const status = String(doc.status ?? doc.caseStatus ?? '');
    return ['seriesCompleted', 'closedOut', 'caseCompleted', 'completed', 'closed'].includes(status);
  }
  if (outputKey === 'Violations.openCount') {
    // Canonical classification emitted by unwindViolationRows — a shell row
    // (violation with zero occurrences) is resolved, so it never counts open.
    return doc.unresolved === true;
  }
  if (outputKey === 'Violations.correctedCount') {
    // Resolved AND real — a removed occurrence's shell is resolved, not
    // corrected; only genuine occurrence rows count.
    return doc.unresolved === false && doc.hasOccurrences === true;
  }
  return true;
}

function aggregate(values: any[], aggregation: string): number {
  // Array fields (e.g. `events` on InspectionSeries docs) aggregate as their
  // element count — Number([]) is NaN and silently zeroed every sum/avg over
  // event arrays.
  const nums = values
    .map((v) => (Array.isArray(v) ? v.length : Number(v)))
    .filter((n) => !isNaN(n));
  switch (aggregation) {
    case 'count':        return values.length;
    case 'countDistinct': return new Set(values.map(String)).size;
    case 'sum':          return nums.reduce((a, b) => a + b, 0);
    case 'avg':          return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
    case 'min':          return nums.length ? Math.min(...nums) : 0;
    case 'max':          return nums.length ? Math.max(...nums) : 0;
    default:             return values.length;
  }
}

// ---------------------------------------------------------------------------
// Flatten violations out of cases for the 'violations' virtual collection.
//
// Delegates the canonical traversal to the shared unwinder
// (@firesquirrel/report-template-resolver) — violations live nested in
// case.responses[fieldId].violation.occurrences[] (NOT case.events[].violations,
// which was the legacy assumption and produced empty results for every
// Violations-cube widget). The map below renames the unwinder's field names
// into the `_`-prefixed context keys the Violations cube's dexieField bindings
// expect.
// ---------------------------------------------------------------------------

function flattenViolations(cases: Record<string, any>[]): Record<string, any>[] {
  return unwindViolationRows(cases).map((row) => ({
    ...row,
    _id: row.occurrenceId ?? row.violationId,
    _caseId: row.caseId,
    _entityId: row.entityId,
    _eventId: undefined,
    _eventType: undefined,
    _eventDate: row.eventDate ?? row.discoveredAt,
    _caseStatus: row.caseStatus,
    _caseType: row.caseType,
    codeSection: row.code,
    codeSetId: row.codeSetId,
    entityName: row.entityName,
    caseNumber: row.caseNumber,
  }));
}

// ---------------------------------------------------------------------------
// Main query executor
// ---------------------------------------------------------------------------

async function readCollectionDocs(
  db: TenantDatabase | Record<string, any>,
  collectionName: string,
): Promise<Record<string, any>[]> {
  const anyDb = db as Record<string, any>;
  const rxCol = anyDb.collections?.[collectionName];
  if (rxCol && typeof rxCol.find === 'function') {
    const docs = await rxCol.find().exec();
    return (docs as any[])
      .filter(Boolean)
      .map((d) => (typeof d.toJSON === 'function' ? d.toJSON() : d))
      .filter((d) => !d?._deleted);
  }
  const table = anyDb[collectionName] ?? rxCol;
  if (table && typeof table.toArray === 'function') {
    const docs = await table.toArray();
    return (docs as any[]).filter((d) => d && !d._deleted);
  }
  return [];
}

/**
 * @process executeDexieQuery
 * Steps:   1. Load raw documents from the target Dexie collection (or flatten violations from `cases`).
 *          2. Apply `timeDimension` date range and `filters`.
 *          3. Group by `groupBy` dimensions and aggregate `measures`.
 *          4. Apply sorting, limit, and compute column totals.
 * Inputs:  db (TenantDatabase), query (DexieQuery).
 * Outputs: DexieQueryResult.
 * Failure Handling: Missing collections return an empty result with `rowCount: 0`.
 * Idempotency: REQUIRED — pure read.
 * @localFirst Source of Truth: local (Dexie). No API call.
 */
export async function executeDexieQuery(
  db: TenantDatabase,
  query: DexieQuery,
): Promise<DexieQueryResult> {
  const startMs = performance.now();

  // 1. Load raw documents from the appropriate Dexie table
  let rawDocs: Record<string, any>[] = [];
  if (query.collection === 'violations') {
    const caseDocs = await readCollectionDocs(db, 'cases');
    rawDocs = flattenViolations(caseDocs);
  } else {
    rawDocs = await readCollectionDocs(db, query.collection);
  }

  // 2. Apply timeDimension date range filter
  if (query.timeDimension?.dateRange) {
    const range = resolveDateRange(query.timeDimension.dateRange);
    if (range) {
      const [from, to] = range;
      rawDocs = rawDocs.filter(doc => {
        const val = getNestedValue(doc, query.timeDimension!.field);
        if (!val) return false;
        const d = new Date(val).getTime();
        return d >= from.getTime() && d <= to.getTime();
      });
    }
  }

  // 3. Apply filters
  for (const filter of query.filters) {
    rawDocs = rawDocs.filter(doc => evaluateFilter(doc, filter));
  }

  // 4. Build rows — group by dimensions if groupBy is specified
  const groupByFields = query.groupBy ?? [];
  let rows: Record<string, any>[];

  if (groupByFields.length > 0) {
    // Group documents by the groupBy key combination
    const dimByField = new Map(query.dimensions.map(d => [d.field, d]));
    const groups = new Map<string, Record<string, any>[]>();
    for (const doc of rawDocs) {
      const key = groupByFields
        .map(f => {
          if (f === '_timeDim' && query.timeDimension) {
            const val = getNestedValue(doc, query.timeDimension.field);
            return val ? truncateToGranularity(new Date(val), query.timeDimension.granularity) : '';
          }
          return String(resolveDimensionValue(doc, f, dimByField.get(f)?.fallbackFields) ?? '');
        })
        .join('||');
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(doc);
    }

    rows = Array.from(groups.entries()).map(([, groupDocs]) => {
      const row: Record<string, any> = {};

      // Dimension values (from first doc in group)
      for (const dim of query.dimensions) {
        row[dim.outputKey] = resolveDimensionValue(groupDocs[0], dim.field, dim.fallbackFields);
      }

      // timeDimension grouping key
      if (query.timeDimension) {
        const firstVal = getNestedValue(groupDocs[0], query.timeDimension.field);
        row[`_timeDim`] = firstVal ? truncateToGranularity(new Date(firstVal), query.timeDimension.granularity) : null;
      }

      // Measure aggregations (respecting per-measure status conditions when mixed)
      for (const measure of query.measures) {
        const scopedDocs = groupDocs.filter(d => matchesMeasureCondition(d, measure.outputKey));
        const values = scopedDocs.map(d => getNestedValue(d, measure.field));
        row[measure.outputKey] = aggregate(values, measure.aggregation);
      }

      return row;
    });
  } else {
    // No grouping — one row per document
    rows = rawDocs.map(doc => {
      const row: Record<string, any> = {
        status: doc.status,
        caseStatus: doc.caseStatus,
        correctionStatus: doc.correctionStatus,
        unresolved: doc.unresolved,
        hasOccurrences: doc.hasOccurrences,
      };
      for (const dim of query.dimensions) {
        row[dim.outputKey] = resolveDimensionValue(doc, dim.field, dim.fallbackFields);
      }
      if (query.timeDimension) {
        const val = getNestedValue(doc, query.timeDimension.field);
        row[`_timeDim`] = val ? truncateToGranularity(new Date(val), query.timeDimension.granularity) : null;
      }
      for (const measure of query.measures) {
        row[measure.outputKey] = getNestedValue(doc, measure.field);
      }
      return row;
    });
  }

  // 5. Sort
  if (query.sort?.length) {
    rows.sort((a, b) => {
      for (const s of query.sort!) {
        const av = a[s.member];
        const bv = b[s.member];
        if (av == null && bv == null) continue;
        if (av == null) return s.direction === 'asc' ? -1 : 1;
        if (bv == null) return s.direction === 'asc' ? 1 : -1;
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        if (cmp !== 0) return s.direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }

  // 6. Compute measure totals over ALL filtered docs — before the display `limit`
  //    slices rows. Ungrouped queries materialize one row per doc, so slicing first
  //    would silently cap aggregates (e.g. a count over >100 docs returned 100).
  //    `matchesMeasureCondition` keeps per-measure scoping (openCount/closedCount/
  //    activeCount/…) consistent between grouped and ungrouped paths.
  const totals: Record<string, any> = {};
  for (const measure of query.measures) {
    const scopedDocs = rawDocs.filter(d => matchesMeasureCondition(d, measure.outputKey));
    const vals = scopedDocs.map(d => getNestedValue(d, measure.field));
    totals[measure.outputKey] = aggregate(vals, measure.aggregation);
  }

  // 7. Limit (display rows only — totals above are computed over the full set)
  const totalBeforeLimit = rows.length;
  if (query.limit && query.limit > 0) {
    rows = rows.slice(0, query.limit);
  }

  // 8. Build column metadata
  const columns: ColumnMeta[] = [
    ...query.dimensions.map(d => ({
      key: d.outputKey,
      label: d.label,
      type: d.type,
      isMeasure: false,
    })),
    ...query.measures.map(m => ({
      key: m.outputKey,
      label: m.label,
      type: 'number' as const,
      isMeasure: true,
    })),
  ];

  if (query.timeDimension) {
    columns.unshift({
      key: '_timeDim',
      label: query.timeDimension.granularity.charAt(0).toUpperCase() + query.timeDimension.granularity.slice(1),
      type: 'string',
      isMeasure: false,
    });
  }

  const queryTimeMs = Math.round(performance.now() - startMs);

  return {
    columns,
    rows,
    totals: Object.keys(totals).length > 0 ? totals : undefined,
    meta: {
      rowCount: totalBeforeLimit,
      queryTimeMs,
      isLocalData: true,
      isSimplifiedPreview: query.collection === 'violations' || (query.groupBy?.length ?? 0) > 2,
    },
  };
}
